import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Modal from '../shared/modal'
import { t } from '../../i18n'
import { LocalizationContext } from '../shared/contexts'
import { formatTenantDateTime } from '../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../utils/timeSource'

const scope = 'controls'

export interface SlotAlertsContext {
  partType: string
  partId: number
  slotLabel: string
}

interface AlertRow {
  id: number
  message: string
  level: string
  sent_at: string
  is_business: boolean
}

interface AlertsResponse {
  alerts: AlertRow[]
  has_more: boolean
  page: number
}

const LEVEL_BADGE_CLASS: Record<string, string> = {
  debug: 'bg-secondary-subtle text-secondary-emphasis',
  info: 'bg-info-subtle text-info-emphasis',
  warning: 'bg-warning-subtle text-warning-emphasis',
  error: 'bg-danger-subtle text-danger-emphasis',
  fatal: 'bg-danger text-white',
}


interface Props {
  context: SlotAlertsContext | null
  onClose: () => void
}

const SlotAlertsModal = ({ context, onClose }: Props) => {
  const [alerts, setAlerts] = useState<AlertRow[]>([])
  const [page, setPage] = useState<number>(0)        // 0 = nothing loaded yet
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const localization = useContext(LocalizationContext)

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // `loading`/`hasMore` would otherwise become stale inside the observer callback.
  const loadingRef = useRef(loading)
  const hasMoreRef = useRef(hasMore)
  loadingRef.current = loading
  hasMoreRef.current = hasMore

  const loadPage = useCallback(async (nextPage: number) => {
    if (!context) return
    setLoading(true)
    setError(false)
    try {
      const { data } = await axios.get<AlertsResponse>('/controls/sentry_alerts', {
        params: { part_type: context.partType, part_id: context.partId, page: nextPage },
      })
      setAlerts(prev => nextPage === 1 ? data.alerts : [...prev, ...data.alerts])
      setPage(data.page)
      setHasMore(data.has_more)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [context?.partType, context?.partId])

  useEffect(() => {
    if (!context) return
    setAlerts([])
    setPage(0)
    setHasMore(true)
    loadPage(1)
  }, [context?.partType, context?.partId, loadPage])

  useEffect(() => {
    if (!sentinelRef.current) return
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]
      if (!entry?.isIntersecting) return
      if (loadingRef.current || !hasMoreRef.current) return
      loadPage(page + 1)
    }, { rootMargin: '120px' })
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [page, loadPage])

  if (!context) return null

  const renderBody = () => {
    if (page === 0 && loading) {
      return <div className="text-muted">{t('slot_alerts_loading', { scope })}</div>
    }
    if (page === 0 && error) {
      return <div className="text-danger">{t('slot_alerts_failed', { scope })}</div>
    }
    if (alerts.length === 0) {
      return <div className="text-muted">{t('slot_alerts_empty', { scope })}</div>
    }

    return (
      <>
        <ul className="list-unstyled mb-0">
          {alerts.map(alert => (
            <li key={alert.id} className="border-bottom py-3">
              <div className="d-flex align-items-center mb-1" style={{ gap: '0.5rem' }}>
                <span className={`badge rounded-pill ${LEVEL_BADGE_CLASS[alert.level] || 'bg-secondary-subtle text-secondary-emphasis'}`}>
                  {alert.level}
                </span>
                <small className="text-muted" title={timeSourceTitle(alert.sent_at, TIME_SOURCE.app)}>{formatTenantDateTime(alert.sent_at, localization)}</small>
              </div>
              <div>{alert.message}</div>
            </li>
          ))}
        </ul>

        {hasMore && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />}

        {hasMore && loading && (
          <div className="text-muted py-3 text-center">{t('slot_alerts_loading_more', { scope })}</div>
        )}
        {error && alerts.length > 0 && (
          <div className="text-danger py-3 text-center">{t('slot_alerts_failed', { scope })}</div>
        )}
      </>
    )
  }

  return (
    <Modal
      headerText={t('slot_alerts_modal_title', { scope, slot: context.slotLabel })}
      onClose={onClose}
      widen
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {renderBody()}
      </div>
    </Modal>
  )
}

export default SlotAlertsModal
